<template>
  <div>
    <br>
    <!-- Botón para mostrar/ocultar el panel CSV -->
    <button
      class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 mb-2"
      @click="mostrarCSV = !mostrarCSV"
    >
      <span class="material-icons" style="font-size:16px">upload_file</span>
      {{ mostrarCSV ? 'Ocultar carga CSV' : 'Cargar desde CSV' }}
    </button>

    <!-- Panel CSV -->
    <div v-if="mostrarCSV" class="border rounded p-3 bg-light mb-3">
      <div class="d-flex align-items-center gap-2 mb-3">
        <span class="material-icons text-secondary" style="font-size:20px">upload_file</span>
        <span class="fw-bold text-secondary small">Carga masiva desde CSV</span>
        <!-- Descargar plantilla -->
        <button
          class="btn btn-outline-secondary btn-sm ms-auto d-flex align-items-center gap-1"
          @click="descargarPlantilla"
        >
          <span class="material-icons" style="font-size:14px">download</span>
          Plantilla CSV
        </button>
      </div>

      <div class="row g-2 align-items-start">
        <!-- Selector archivo -->
        <div class="col-12 col-md-6">
          <label class="form-label form-label-sm fw-bold mb-1">Archivo CSV</label>
          <input
            type="file"
            class="form-control form-control-sm"
            accept=".csv"
            ref="inputCSV"
            @change="leerCSV"
            :class="{ 'is-invalid': errorCSV }"
          />
          <div class="invalid-feedback">{{ errorCSV }}</div>
          <small class="text-muted">Formato: <code>codigo;valor</code> — separador punto y coma</small>
        </div>

        <!-- Botón procesar -->
        <div class="col-12 col-md-3 d-flex align-items-end">
          <button
            class="btn btn-secondary btn-sm w-100 d-flex align-items-center justify-content-center gap-1"
            @click="procesarCSV"
            :disabled="cargandoCSV || filas.length === 0"
          >
            <span v-if="cargandoCSV" class="spinner-border spinner-border-sm"></span>
            <span v-else class="material-icons" style="font-size:16px">send</span>
            {{ cargandoCSV ? 'Procesando...' : `Procesar (${filas.length})` }}
          </button>
        </div>
      </div>

      <!-- Preview de filas leídas -->
      <div v-if="filas.length > 0" class="mt-3">
        <p class="small fw-bold mb-1">
          Vista previa — {{ filas.length }} registros:
        </p>
        <div style="max-height:150px; overflow-y:auto;">
          <table class="table table-sm table-bordered table-hover mb-0">
            <thead class="table-dark">
              <tr>
                <th class="small">#</th>
                <th class="small">Código</th>
                <th class="small">Valor</th>
                <th class="small">Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(fila, i) in filas" :key="i">
                <td class="small">{{ i + 1 }}</td>
                <td class="small">{{ fila.codigo }}</td>
                <td class="small">{{ fila.valor }}</td>
                <td class="small">
                  <span v-if="fila.resultado === 'ok-insert'"
                    class="badge bg-success">✓ Insertado</span>
                  <span v-else-if="fila.resultado === 'ok-update'"
                    class="badge bg-warning text-dark">✎ Actualizado</span>
                  <span v-else-if="fila.resultado === 'error'"
                    class="badge bg-danger" :title="fila.error">✗ Error</span>
                  <span v-else
                    class="badge bg-light text-dark">Pendiente</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Resumen final -->
      <div v-if="resumen.total > 0" class="mt-2 d-flex gap-2 flex-wrap">
        <span class="badge bg-success">✓ Insertados: {{ resumen.insertados }}</span>
        <span class="badge bg-warning text-dark">✎ Actualizados: {{ resumen.actualizados }}</span>
        <span class="badge bg-danger">✗ Errores: {{ resumen.errores }}</span>
        <span class="badge bg-secondary">Total: {{ resumen.total }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import api  from "@/services/api";
import Swal from "sweetalert2";

export default {
  name: "TarifaPresCSV",
  props: {
    p_idPrestador: { type: Number, required: true },
  },
  data() {
    return {
      mostrarCSV:  false,
      filas:       [],
      errorCSV:    "",
      cargandoCSV: false,
      resumen: {
        total:       0,
        insertados:  0,
        actualizados: 0,
        errores:     0,
      },
    };
  },
  methods: {
    // ── Leer y parsear CSV ──────────────────────────────────────
    leerCSV(event) {
      this.filas    = [];
      this.errorCSV = "";
      this.resumen  = { total: 0, insertados: 0, actualizados: 0, errores: 0 };

      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const lineas = e.target.result
          .split("\n")
          .map(l => l.trim())
          .filter(l => l.length > 0);

        // Saltar encabezado
        const datos = lineas[0].toLowerCase().startsWith("codigo") 
          ? lineas.slice(1) 
          : lineas;

        const filasParsed = [];
        const erroresParseo = [];

        datos.forEach((linea, i) => {
          const partes = linea.split(";");
          if (partes.length < 2) {
            erroresParseo.push(`Línea ${i + 2}: formato inválido`);
            return;
          }
          const codigo = partes[0].trim();
          const valor  = parseFloat(partes[1].trim().replace(",", "."));

          if (!codigo) {
            erroresParseo.push(`Línea ${i + 2}: código vacío`);
            return;
          }
          if (isNaN(valor) || valor < 0) {
            erroresParseo.push(`Línea ${i + 2}: valor inválido (${partes[1]})`);
            return;
          }
          filasParsed.push({ codigo, valor, resultado: null, error: null });
        });

        if (erroresParseo.length > 0) {
          this.errorCSV = erroresParseo.join(" | ");
          return;
        }
        this.filas = filasParsed;
      };
      reader.readAsText(file, "UTF-8");
    },

    // ── Procesar fila por fila ──────────────────────────────────
    async procesarCSV() {
      if (this.filas.length === 0) return;
      this.cargandoCSV = true;
      this.resumen     = { total: 0, insertados: 0, actualizados: 0, errores: 0 };

      for (const fila of this.filas) {
        try {
          const response = await api.post("/procedimientos/insertar-tarifa-pres", null, {
            params: {
              idPrestador: this.p_idPrestador,
              codigo:      fila.codigo,
              valor:       fila.valor,
            },
          });
          const { insertados, actualizados } = response.data;
          fila.resultado = actualizados > 0 ? "ok-update" : "ok-insert";
          this.resumen.insertados  += insertados  ?? 0;
          this.resumen.actualizados += actualizados ?? 0;
        } catch (error) {
          fila.resultado = "error";
          fila.error     =
            typeof error.response?.data === "string"
              ? error.response.data
              : error.response?.data?.message ?? error.message ?? "Error";
          this.resumen.errores++;
        }
        this.resumen.total++;
      }

      this.cargandoCSV = false;

      Swal.fire({
        icon: this.resumen.errores > 0 ? "warning" : "success",
        title: "Carga completada",
        html: `
          <div class="text-start">
            <p>✓ Insertados: <strong>${this.resumen.insertados}</strong></p>
            <p>✎ Actualizados: <strong>${this.resumen.actualizados}</strong></p>
            <p>✗ Errores: <strong>${this.resumen.errores}</strong></p>
            <p>Total procesados: <strong>${this.resumen.total}</strong></p>
          </div>`,
      });
    },

    // ── Descargar plantilla ─────────────────────────────────────
    descargarPlantilla() {
      const contenido  = "codigo;valor\n381121;10.00\n381122;15.00\n";
      const link       = document.createElement("a");
      link.href        = URL.createObjectURL(
        new Blob(["\uFEFF" + contenido], { type: "text/csv;charset=utf-8;" })
      );
      link.download    = "plantilla_tarifas.csv";
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>